import React, { useState } from "react";
import { GiftCardPurchaseOption } from "../../../../types/gift-card";
import styled from "styled-components";
import { breakpoints } from "../../../../utils/styles";
import { centsToCurrencyString } from "../../../../utils/numbers";

const AmountInputContainer = styled.div`
    margin: 3.5rem auto;
    max-width: 75%;
    text-align: center;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        margin-left: 0;
        margin-right: 0;
        max-width: 21rem;
        text-align: left;
    }
`;

const AmountLabel = styled.label`
    display: block;
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
    line-height: 1.4;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 1rem;
        margin-bottom: 0.5rem;
    }
`;

const AmountInputWrapper = styled.div`
    align-items: center;
    display: flex;
    margin: 0 auto;
    position: relative;
    width: 180px;
`;

const CurrencySymbol = styled.span`
    color: var(--text-primary);
    font-family: var(--ff-title);
    font-size: 4rem;
    font-weight: 600;
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 2rem;
    }
`;

const AmountInput = styled.input<{ error?: boolean }>`
    background: var(--page-bg);
    border: 0;
    border-bottom: 1px solid ${props => props.error ? 'var(--danger)' : 'var(--text-primary)'};
    color: var(--text-primary);
    font-family: var(--ff-title);
    font-size: 4rem;
    padding: 1rem 2rem 1rem;
    text-align: center;
    width: 100%;
    
    &:focus {
        outline: none;
        border-color: ${props => props.error ? 'var(--danger)' : 'var(--text-primary)'};
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 2rem;
    }
`;

const ErrorMessage = styled.p`
    color: var(--danger);
    font-size: 0.875rem;
    margin-top: 0.5rem;
    margin-bottom: 0;
    text-align: center;
`;

interface Props {
    selectedDesign: GiftCardPurchaseOption;
    onSubmit: (amount: number | null) => void;
}

const WalletBuyAmountInput: React.FC<Props> = ({
    selectedDesign,
    onSubmit
}) => {
    const [amount, setAmount] = useState<string>("");
    const [displayValue, setDisplayValue] = useState<string>("");
    const [error, setError] = useState<string>("");

    const minAmount = selectedDesign.minPrice ? selectedDesign.minPrice / 100 : 0;
    const maxAmount = selectedDesign.maxPrice ? selectedDesign.maxPrice / 100 : 0;

    const formatNumberWithCommas = (value: string): string => {
        if (!value) return "";

        const parts = value.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const cleanValue = inputValue.replace(/[^0-9]/g, '');

        setAmount(cleanValue);
        setDisplayValue(formatNumberWithCommas(cleanValue));

        const numValue = parseInt(cleanValue, 10);

        onSubmit(null);

        if (!cleanValue) {
            setError("");
            return;
        }

        if (isNaN(numValue)) {
            setError("Ingresa un monto válido");
            return;
        }

        if (numValue < minAmount) {
            setError(`El monto mínimo es $${formatNumberWithCommas(minAmount.toFixed(2))}`);
            return;
        }

        if (numValue > maxAmount) {
            setError(`El monto máximo es $${formatNumberWithCommas(maxAmount.toFixed(2))}`);
            return;
        }

        setError("");

        onSubmit(numValue * 100);
    };

    return (
        <AmountInputContainer>
            <AmountLabel>Por favor <strong>escribe el monto</strong> que deseas cargar en la tarjeta:</AmountLabel>
            <AmountInputWrapper>
                <CurrencySymbol>$</CurrencySymbol>
                <AmountInput
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={displayValue}
                    onChange={handleAmountChange}
                    error={!!error}
                    maxLength={maxAmount.toString().length + 3} // +3 for possible decimals and comma
                />
            </AmountInputWrapper>
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </AmountInputContainer>
    );
};

export default WalletBuyAmountInput;

